/**
 * Cómo se escribe el texto que ve el dueño.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ UNA MAYÚSCULA IMPORTA TANTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Softfi le pide a un dueño que le confíe el dinero de su negocio. Un aviso
 * que empieza en minúscula —«tasa guardada»— no es un detalle de estilo: es la
 * señal de que **quien lo hizo no revisó**. Y si no revisó el texto, la
 * pregunta siguiente, la que de verdad importa, es si revisó la cuenta.
 *
 * Un programa gratis se perdona. Uno que cobra y toca la caja, no.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ Y POR QUÉ VA AQUÍ Y NO CORRIGIENDO CADA SITIO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Los avisos vienen de tres sitios: escritos aquí, traídos del servidor y
 * escritos por el propio dueño (el motivo de una devolución). Repasar los
 * primeros a mano arregla hoy y no arregla el que se escriba mañana, ni los
 * otros dos, que son justo los que no se pueden repasar.
 */

/**
 * La primera letra en mayúscula, sin tocar el resto.
 *
 * ⚠️ El resto se deja **tal cual** a propósito. Pasarlo a minúsculas
 * destrozaría los nombres propios —«harina pan», «coca cola»— y los códigos
 * que el dueño escribe en mayúscula porque así vienen en la factura.
 */
export function conMayuscula(texto: string): string {
  const limpio = texto.trimStart();
  if (!limpio) return texto;

  // `toLocaleUpperCase` y no `toUpperCase`: hay idiomas donde no es lo mismo,
  // y el texto de un dueño puede empezar por una letra acentuada.
  return limpio.charAt(0).toLocaleUpperCase("es") + limpio.slice(1);
}

/**
 * Una ETIQUETA, con su primera letra en mayúscula esté donde esté.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ ES DISTINTA DE `conMayuscula`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Una etiqueta no es una frase: es un rótulo. Y muchas empiezan por un número
 * —«2 por revisar», «4 negocios», «3 en cocina»—, así que subir el primer carácter
 * no hace nada y se quedan en minúscula, que es justo lo que se ve descuidado.
 *
 * Aquí se busca la primera LETRA y se sube esa: «2 Por revisar».
 *
 * Y por eso no vale para frases: «3 tasas llevan más de un día sin
 * actualizarse» se convertiría en «3 Tasas llevan…», que está mal escrito. Las
 * frases usan `conMayuscula`; los rótulos, esto.
 */
export function comoEtiqueta(texto: string): string {
  const i = texto.search(/\p{L}/u);
  if (i < 0) return texto;
  return texto.slice(0, i) + texto.charAt(i).toLocaleUpperCase("es") + texto.slice(i + 1);
}

/**
 * «1 día» / «3 días», sin tener que acordarse cada vez.
 *
 * ⚠️ Un «hace 1 días» en la pantalla del fiado no es una errata cualquiera: es
 * la que se ve en la pantalla donde el dueño está mirando quién le debe
 * dinero, y ahí lo último que conviene es parecer descuidado.
 */
export function plural(cantidad: number, singular: string, plural_: string): string {
  return `${cantidad} ${cantidad === 1 ? singular : plural_}`;
}

/**
 * El nombre de una medida en plural y en minúscula: «Caja» → «cajas», «Galón»
 * → «galones», «Caja de 6» → «cajas de 6». Para «Cantidad de cajas».
 *
 * Lo escribe el dueño, así que es una regla sencilla y no un diccionario: solo
 * cambia la primera palabra, que es la que lleva el plural.
 */
export function medidaEnPlural(nombre: string): string {
  const [primera = "", ...resto] = nombre.trim().toLocaleLowerCase("es").split(" ");
  let plural_: string;
  if (/ón$/.test(primera)) plural_ = primera.slice(0, -2) + "ones";
  else if (/z$/.test(primera)) plural_ = primera.slice(0, -1) + "ces";
  else if (/[sx]$/.test(primera)) plural_ = primera;
  else if (/[lrndj]$/.test(primera)) plural_ = primera + "es";
  else plural_ = primera + "s";
  return [plural_, ...resto].join(" ");
}

/**
 * El texto libre que escribe el dueño, listo para guardar.
 *
 * Quita los espacios de los bordes y sube la primera letra. Es lo que se aplica
 * al motivo de una devolución, que se escribe con prisa y con el cliente
 * delante —«estaba vencida»— y después se lee en un recibo.
 */
export function normalizarTexto(texto: string): string {
  return conMayuscula(texto.trim());
}

// ── El nombre con las mayúsculas en su sitio ─────────────────────────────────
//
// ⚠️ LA MISMA REGLA QUE EL SERVIDOR (`app/core/nombres.py`), con las mismas
// listas y los mismos pasos: allí está explicada. Existe aquí porque sin
// internet el cliente se guarda en el aparato y el recibo se imprime en el
// momento, antes de que el servidor lo arregle. Las pruebas de los dos lados
// usan la misma tabla de ejemplos.

const PARTICULAS = new Set([
  "de", "del", "la", "las", "los", "y", "e", "o", "u", "a", "al", "di", "van", "von", "con",
  "sin", "en", "para", "por", "x",
]);

const SIGLAS = new Map(
  [
    "USB", "LED", "LCD", "TV", "HD", "FHD", "UHD", "HDMI", "VGA", "DVD", "CD", "PC", "CPU",
    "GPU", "RAM", "SSD", "HDD", "SD", "GPS", "RGB", "AUX", "NFC", "SIM", "UV", "PVC", "CPVC",
    "PPR", "AWG", "SAE", "AA", "AAA", "XS", "XL", "XXL", "XXXL", "HP", "LG", "JBL", "AMD",
    "AOC", "KFC", "BBQ", "DJ", "VIP", "BCV", "RIF", "CA", "SA", "SRL",
    "WiFi", "iPhone", "iPad", "iPod", "iMac", "MacBook", "AirPods", "PlayStation",
    "WhatsApp", "YouTube", "TikTok", "PayPal",
  ].map((s) => [s.toLowerCase(), s]),
);

const UNIDADES = new Map(
  [
    "kg", "g", "gr", "grs", "mg", "ml", "cc", "L", "lt", "lts", "oz", "lb", "lbs", "cm", "mm",
    "m", "mts", "km", "pulg", "pza", "pzas", "pz", "und", "unid",
    "KB", "MB", "GB", "TB", "mAh", "W", "kW", "V", "Hz", "kHz", "MHz", "GHz",
  ].map((u) => [u.toLowerCase(), u]),
);

const ORDINALES = new Set(["ra", "da", "er", "ro", "do", "to", "vo", "mo", "no"]);

/** Lo que parte un nombre en dos, con un espacio al lado: «Libro - La Metamorfosis». */
const CORTES = new Set(["-", "–", "—", ":", "/", "|", "·", "•"]);

const PALABRAS = /([\p{L}\p{M}\p{N}_'’]+)/u;
const APOSTROFO = /(['’])/;
const NUMERO_Y_UNIDAD = /^(\p{Nd}+)(\p{L}+)$/u;
const POR = /^\p{Nd}+[xX]\p{Nd}+$/u;

const conCifras = (p: string) => /\p{Nd}/u.test(p);
const esMinuscula = (p: string) => p === p.toLowerCase() && p !== p.toUpperCase();
const esMayuscula = (p: string) => p === p.toUpperCase() && p !== p.toLowerCase();
const en = (lista: string[], i: number) => lista[i] ?? "";
const capital = (p: string) => p.slice(0, 1).toUpperCase() + p.slice(1).toLowerCase();
const trasUnCorte = (antes: string) => {
  const marca = antes.trim();
  return marca !== "" && CORTES.has(marca.slice(-1)) && marca !== antes;
};

/** «iPhone», «McDonald», «PlayStation», «PCs»: mayúsculas a propósito. */
function bienCombinada(p: string): boolean {
  if (p.length > 2 && esMayuscula(p.slice(0, -1)) && p.endsWith("s")) return true;
  let i = esMinuscula(p.charAt(0)) ? 1 : 0;
  let tramos = 0;
  while (i < p.length) {
    if (!esMayuscula(p.charAt(i))) return false;
    let j = i + 1;
    while (j < p.length && esMinuscula(p.charAt(j))) j++;
    if (j === i + 1) return false;
    tramos++;
    i = j;
  }
  return tramos >= (esMinuscula(p.charAt(0)) ? 1 : 2);
}

/** «1KG» → «1kg», «32gb» → «32GB», «ps5» → «PS5», «40X50» → «40x50»; «A4» se queda. */
function conNumeros(p: string): string {
  if (POR.test(p)) return p.toLowerCase();
  const medida = NUMERO_Y_UNIDAD.exec(p);
  if (medida) {
    const letras = (medida[2] ?? "").toLowerCase();
    const unidad = ORDINALES.has(letras) ? letras : UNIDADES.get(letras);
    return unidad ? (medida[1] ?? "") + unidad : p;
  }
  if (/^\p{L}/u.test(p) && esMinuscula(p)) {
    if ((p.match(/\p{L}/gu) ?? []).length <= 3) return p.toUpperCase();
    return p.charAt(0).toUpperCase() + p.slice(1);
  }
  return p;
}

interface Lugar {
  primera: boolean;
  ultima: boolean;
  grita: boolean;
  antes: string;
  despues: string;
  trasNumero: boolean;
}

function letras(p: string, { primera, ultima, grita, antes, despues, trasNumero }: Lugar): string {
  const minuscula = p.toLowerCase();
  const unidad = UNIDADES.get(minuscula);
  if (trasNumero && unidad) return unidad;
  const sigla = SIGLAS.get(minuscula);
  if (sigla) return sigla;
  const particula = !primera && PARTICULAS.has(minuscula) && !trasUnCorte(antes);

  if (p.length === 1) {
    if ([".", "'", "’"].includes(despues.slice(0, 1))) return p.toUpperCase(); // «C.A.», «D'Onofrio»
    if (antes.slice(-1) === "/" || despues.slice(0, 1) === "/") return p; // «c/u», «s/n»
    if (particula && !ultima && (esMinuscula(p) || grita)) return minuscula;
    return p.toUpperCase(); // «Vitamina C»
  }

  if (esMinuscula(p)) return particula ? p : p.charAt(0).toUpperCase() + p.slice(1);
  if (esMayuscula(p.charAt(0)) && esMinuscula(p.slice(1))) return p;
  if (esMayuscula(p)) {
    if (particula) return minuscula;
    if (!grita && p.length <= 3) return p; // «Harina PAN»: una sigla a propósito
    return capital(p);
  }
  if (bienCombinada(p)) return p;
  return particula ? minuscula : capital(p);
}

/**
 * «HARINA PAN» → «Harina Pan»; «aceite de girasol» → «Aceite de Girasol»;
 * «cable usb tipo c» → «Cable USB Tipo C»; «iPhone 15» se queda.
 */
export function nombreConMayusculas(nombre: string): string {
  const texto = nombre.split(/\s+/).filter(Boolean).join(" ");
  // `split` con paréntesis alterna separador y palabra: las palabras son las
  // posiciones impares.
  const partes = texto.split(PALABRAS);
  const posiciones: number[] = [];
  for (let i = 1; i < partes.length; i += 2) posiciones.push(i);
  const grita = !posiciones.some((i) => !conCifras(en(partes, i)) && /\p{Ll}/u.test(en(partes, i)));
  const ultima = partes.length - 2;

  for (const i of posiciones) {
    const palabra = en(partes, i);
    const antes = en(partes, i - 1);
    const despues = en(partes, i + 1);
    if (conCifras(palabra)) {
      partes[i] = conNumeros(palabra);
      continue;
    }
    // «TW-2», «SM-A105»: unido con guion a algo con números es un código.
    const siguiente = i + 2 < partes.length ? en(partes, i + 2) : "";
    const anterior = i > 1 ? en(partes, i - 2) : "";
    if ((despues === "-" && conCifras(siguiente)) || (antes === "-" && conCifras(anterior))) continue;
    const trasNumero = i > 1 && antes === " " && /\p{Nd}$/u.test(en(partes, i - 2));
    const trozos = palabra.split(APOSTROFO);
    for (let k = 0; k < trozos.length; k += 2) {
      const trozo = en(trozos, k);
      if (!trozo) continue;
      if (k > 0 && k === trozos.length - 1 && (trozo === "s" || trozo === "S")) {
        trozos[k] = "s"; // «McDonald's»
        continue;
      }
      trozos[k] = letras(trozo, {
        primera: i === 1 && k === 0,
        ultima: i === ultima && k === trozos.length - 1,
        grita,
        antes: k === 0 ? antes : en(trozos, k - 1),
        despues: k === trozos.length - 1 ? despues : en(trozos, k + 1),
        trasNumero,
      });
    }
    partes[i] = trozos.join("");
  }
  return partes.join("");
}
